import { StarIcon, UserGroupIcon, ClockIcon, HeartIcon, CheckCircleIcon, ArrowRightIcon, ShieldCheckIcon, AcademicCapIcon } from '@heroicons/react/24/solid';
import aboutUsImage from '../assets/optimized/about-us-image.webp';
import aboutUsFrozen from '../assets/optimized/about-us-image-frozen.webp';
import googleLogo from '../assets/optimized/google-2015-logo-svg.webp';
import veteranBadge from '../assets/optimized/veteranbadge.webp';
import { useContent } from '../context/ContentContext';

const VALUE_ICONS = [StarIcon, UserGroupIcon, ClockIcon, HeartIcon];
const CRED_ICONS = [ShieldCheckIcon, AcademicCapIcon];

export default function About() {
  const { content, settings } = useContent();
  const about = content.about;

  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24 bg-brand-off-white relative overflow-hidden">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20 items-center">
          <div className="lg:col-span-5 relative order-2 lg:order-1">
            <div className="relative rounded-2xl shadow-xl overflow-hidden bg-white group/frost">
              <div className="absolute top-4 left-4 sm:top-[30px] sm:left-[30px] z-10">
                <img decoding="async" loading="lazy" width={200} height={208}
                  src={veteranBadge}
                  alt="Veteran Owned Business"
                  className="w-[72px] sm:w-[93.4px] h-auto drop-shadow-lg"
                />
              </div>
              <div className="aspect-[3/4] sm:aspect-[4/5] relative">
                <img decoding="async" loading="lazy" width={800} height={1067}
                  src={aboutUsImage}
                  alt="Best Neighbor Home Services Team Member"
                  className="w-full h-full object-cover object-center"
                />
                <img decoding="async" loading="lazy" width={800} height={1067}
                  src={aboutUsFrozen}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover object-center opacity-0 group-hover/frost:opacity-100 transition-opacity duration-[1200ms] ease-in-out"
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-[40%] pointer-events-none"
                  style={{
                    background: 'linear-gradient(to top, rgb(217, 119, 6) 0%, rgba(217, 119, 6, 0.7) 30%, rgba(217, 119, 6, 0) 100%)',
                  }}
                />
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
                <div className="hidden sm:grid grid-cols-2 gap-3 mb-4">
                  {about.credentials.map((cred, idx) => {
                    const Icon = CRED_ICONS[idx] || ShieldCheckIcon;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-white/95 backdrop-blur-sm rounded-lg text-left">
                        <Icon className="w-8 h-8 text-brand-orange flex-shrink-0" />
                        <div>
                          <p className="text-xs text-brand-warm-gray mb-0.5">{cred.title}</p>
                          <p className="font-semibold text-brand-navy whitespace-nowrap text-xs">{cred.subtitle}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between p-3 sm:p-3 rounded-xl sm:rounded-lg bg-white/20 backdrop-blur-md border border-white/30">
                  <div className="flex items-center gap-2.5 sm:gap-4">
                    <img decoding="async" loading="lazy" width={192} height={65} src={googleLogo} alt="Google" className="w-auto h-5 sm:h-[22px] brightness-0 invert ml-1 sm:ml-2.5" />
                    <p className="text-base sm:text-xl font-bold text-white">{about.rating}</p>
                    <div className="flex" style={{ gap: '1px' }}>
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <a
                    href={settings.googleReviewsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-brand-orange px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-md text-xs sm:text-sm font-semibold hover:bg-white/90 transition-colors flex-shrink-0"
                  >
                    View All
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-brand-orange rounded-full" />
              <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
                {about.sectionLabel}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
              {about.heading}
            </h2>

            <p className="text-base sm:text-lg text-[#756c65] leading-relaxed mb-5 sm:mb-6">
              {about.description}
            </p>

            <div className="space-y-3 mb-8">
              {about.features.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-brand-orange" />
                  <span className="text-brand-navy font-medium">{item}</span>
                </div>
              ))}
            </div>

            <a
              href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-brand-navy text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-semibold hover:bg-brand-orange transition-colors duration-300 group w-full sm:w-auto"
            >
              <span>{about.buttonLabel}</span>
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        <div className="mt-10 sm:mt-20 lg:mt-24">
          <div className="flex items-center gap-4 mb-6 sm:mb-10">
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-brand-navy">{about.coreValuesHeading}</h3>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
            {about.coreValues.map((value, index) => {
              const Icon = VALUE_ICONS[index] || StarIcon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-7 group hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-brand-orange/20"
                >
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-brand-navy group-hover:bg-brand-orange rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-5 transition-colors duration-300">
                    <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-brand-navy text-sm sm:text-lg mb-1 sm:mb-2">
                    {value.title}
                  </h3>
                  <p className="text-brand-warm-gray text-xs sm:text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
